import { defineComponent, onMounted, watch } from 'vue'
import type { IBaseComponentProps } from '../base/types'
import { FORM_INPUT_FIELD_PROPS, type IInputProps } from '../form/input/types'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import Heading from '@tiptap/extension-heading'
import type { IconFactor } from '@/types/elements/icon'
import Icon from '../icon'
import { twColor } from '@/utils/style'
import { showPrompt } from '../modal/prompt'
import { onClickOutside } from '@vueuse/core'

export type TextEditorProps = IBaseComponentProps & Partial<IInputProps> & {
  onValueChange?: (value: string) => void | Promise<void>;
}

type EditorButtonProps = {
  title?: string;
  icon?: IconFactor;
  onClick?: () => void;
  property: string;
  editor: any;
}

const EditorButton = (props: EditorButtonProps) => {
  return (
    <div
      class={
        `w-6 h-6 rounded flex items-center justify-center py-2 px-3 text-xs hover:bg-primary-100 hover:text-primary-500 cursor-pointer
        font-medium
        ${props.editor?.value?.isActive(props.property) ? 'bg-primary-100 text-primary-500' : 'text-gray-600'}`
      }
      onClick={props.onClick}
    >
      {props.icon && <Icon src={props.icon} />}
      {props.title}
    </div>
  )
}

type SectionTypeDropdownProps = {
  editor: any;
};

const SectionTypeDropdown = (props: SectionTypeDropdownProps) => {
  const getActiveHeading = () => {
    if (props.editor.value?.isActive('heading', { level: 1 })) return 'Heading 1';
    if (props.editor.value?.isActive('heading', { level: 2 })) return 'Heading 2';
    if (props.editor.value?.isActive('heading', { level: 3 })) return 'Heading 3';
    return 'Paragraph';
  };

  const handleSelect = (level: number | 'paragraph') => {
    if (level === 'paragraph') {
      props.editor.value?.chain().focus().setParagraph().run();
    } else {
      props.editor.value?.chain().focus().toggleHeading({ level }).run();
    }
  };

  return (
    <div class="relative group flex flex-col">
      <button
        class="w-24 h-6 group-hover:bg-primary-100 hover:text-primary-500 rounded-sm px-2 flex items-center justify-center gap-2 text-xs text-gray-600 hover:bg-gray-200 cursor-pointer"
      >
        {getActiveHeading()}
        <Icon
          class="mt-0.5"
          size={8}
          fill="currentColor"
          src={async () => await import('assets/img/svg/icons/chevron_down.svg?url')}
        />
      </button>

      <div class="hidden group-hover:block w-32 absolute bg-white text-gray-800 border border-gray-100 rounded-sm shadow-md z-9999 -mt-[122px]">
        <div class="px-3 py-1 hover:bg-primary-100 cursor-pointer text-lg font-bold" onClick={() => handleSelect(1)}>
          Heading 1
        </div>
        <div class="px-3 py-1 hover:bg-primary-100 cursor-pointer text-md font-semibold" onClick={() => handleSelect(2)}>
          Heading 2
        </div>
        <div class="px-3 py-1 hover:bg-primary-100 cursor-pointer text-sm font-medium" onClick={() => handleSelect(3)}>
          Heading 3
        </div>
        <div class="px-3 py-1 hover:bg-primary-100 cursor-pointer text-xs" onClick={() => handleSelect('paragraph')}>
          Paragraph
        </div>
      </div>
    </div>
  );
};

const TextEditor = defineComponent<TextEditorProps>({
  setup(props) {

    const componentRef = ref(null)

    onClickOutside(componentRef, () => {
      if (props.fieldRef && props.onBlur) {
        props.onBlur(new FocusEvent('blur-sm'))
      }
    })

    const CustomHeading = Heading.extend({
      renderHTML({ node, HTMLAttributes }) {
        const level = node.attrs.level || 1;

        return [
          `h${level}`,
          {
            ...HTMLAttributes,
            style: `font-weight: bold; margin: 0.5rem 0; font-size: ${2 - level * 0.25}rem;`
          },
          0
        ];
      }
    });

    watch(() => props.value, (newValue) => {
      if (editor.value && newValue !== editor.value.getHTML()) {
        editor.value.commands.setContent(newValue || '', { emitUpdate: false } as any);
      }
    });

    const editor = useEditor({
      content: props.value || '',
      editorProps: {
        attributes: {
          class: 'focus:outline-hidden text-sm h-full flex-1',
        },
      },
      extensions: [
        CustomHeading.configure({
          levels: [1, 2, 3],
        }),
        StarterKit.configure({
          paragraph: {
            HTMLAttributes: { style: 'margin: 0; font-size: 14px; line-height: 1.5; min-height: 1.5em;' }
          },
          heading: false,
          bold: {
            HTMLAttributes: { style: 'font-weight: bold;' }
          },
          italic: {
            HTMLAttributes: { style: 'font-style: italic;' }
          },
          bulletList: {
            HTMLAttributes: { style: 'list-style-type: disc; padding-left: 1.5rem;' }
          },
          orderedList: {
            HTMLAttributes: { style: 'list-style-type: decimal; padding-left: 1.5rem;' }
          },
          listItem: {
            HTMLAttributes: { style: 'margin: 0.25rem 0;' }
          },
        }),
        Link.configure({
          defaultProtocol: 'https',
          HTMLAttributes: { style: 'color: blue; text-decoration: underline;' }
        }),
        Placeholder.configure({
          placeholder: props.placeholder || 'Write something ...'
        })
      ],
      onUpdate({ editor }) {
        const nextValue = editor.getHTML()
        const fieldRef = props.fieldRef
        const dataRef = props.dataRef
        if (fieldRef) {
          if (props.reportChange) {
            props.reportChange(fieldRef, nextValue as any)
          }
        }
        if (dataRef) {
          dataRef.setValue(nextValue)
        }
      }
    });

    onMounted(() => {
      const style = document.createElement('style');
      style.textContent = `
        .ProseMirror {
          position: relative;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: ${twColor('gray-500')};
          position: absolute;
          top: 0;
          left: 0;
          pointer-events: none;
        }
      `;
      document.head.appendChild(style);
    });

    const setLink = async () => {
      const result = await showPrompt({
        title: 'URL',
        inputLabel: 'Enter URL',
        inputPlaceholder: 'https://',
        inputType: 'url',
        inputRequired: true,
        answerNoText: 'Cancel',
        answerYesText: 'Ok',
        answerYesVariant: 'primary'
      })

      if (typeof result === 'boolean' || !result?.confirmed) {
        return
      }

      let inputValue = result.inputValue

      if (!inputValue || inputValue === null) {
        return
      }

      if (inputValue === '') {
        editor.value?.chain()
          .focus()
          .extendMarkRange('link')
          .unsetLink()
          .run()
        return
      }

      if (!/^https?:\/\//i.test(inputValue)) {
        inputValue = 'https://' + inputValue
      }

      editor.value?.chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: inputValue })
        .run()
    }

    return () => {
      return (
        <div ref={componentRef} class="h-full min-h-[300px] flex flex-col gap-1 border border-gray-300">
          <EditorContent class="p-2 h-full flex-1 flex flex-col max-h-72 overflow-y-auto" editor={editor.value} />
          <div class="flex flex-row items-center gap-1 p-2 bg-gray-100 border-t border-t-gray-300">
            <SectionTypeDropdown editor={editor} />
            {/* Vertical Seperator */}
            <div class="px-1">
              <span class="block w-px h-4 bg-gray-300 dark:bg-gray-600"></span>
            </div>
            <EditorButton
              icon={{
                src: async () => await import('assets/img/svg/icons/letter-bold.svg?url'),
                size: '1rem',
                stroke: 'currentColor'
              }}
              property="bold"
              editor={editor}
              onClick={() => editor.value?.chain().focus().toggleBold().run()}
            />
            <EditorButton
              icon={{
                src: async () => await import('assets/img/svg/icons/letter-italic.svg?url'),
                size: '1rem',
                stroke: 'currentColor'
              }}
              property="italic"
              editor={editor}
              onClick={() => editor.value?.chain().focus().toggleItalic().run()}
            />
            <EditorButton
              icon={{
                src: async () => await import('assets/img/svg/icons/chain-link.svg?url'),
                size: '1rem',
                stroke: 'currentColor'
              }}
              property="link"
              editor={editor}
              onClick={() => {
                if (!editor.value?.isActive('link')) {
                  setLink()
                } else {
                  editor.value?.chain().focus().unsetLink().run()
                }
              }}
            />
            {/* Vertical Seperator */}
            <div class="px-1">
              <span class="block w-px h-4 bg-gray-300 dark:bg-gray-600"></span>
            </div>
            <EditorButton
              icon={{
                src: async () => await import('assets/img/svg/icons/list-bullet.svg?url'),
                size: '1.25rem',
                stroke: 'currentColor'
              }}
              property="bulletList"
              editor={editor}
              onClick={() => editor.value?.chain().focus().toggleBulletList().run()}
            />
            <EditorButton
              icon={{
                src: async () => await import('assets/img/svg/icons/list-ordered.svg?url'),
                size: '1rem',
                stroke: 'currentColor'
              }}
              property="orderedList"
              editor={editor}
              onClick={() => editor.value?.chain().focus().toggleOrderedList().run()}
            />
          </div>
        </div>
      )
    }
  }
})

TextEditor.props = FORM_INPUT_FIELD_PROPS

export default TextEditor
